// ============================================================================
// Componente: AutomacaoValeTransporte.js
// Objetivo: Interface para automação da gestão do Vale Transporte (RH).
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import { 
    Bus, Download, AlertCircle, CheckCircle2, ArrowLeft,
    FileSpreadsheet, Info, FileText, Eraser, HelpCircle,
    UploadCloud, Loader2, X, History
} from 'lucide-react';
import HelpCenter from '../../HelpCenter';

const AutomacaoValeTransporte = ({ embedded = false }) => {
    const navigate = useNavigate();
    
    // Controle de Layout
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);

    const [activeTab, setActiveTab] = useState('auditoria');
    // Estados de Ajuda
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Regras de Perfil do Usuário
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const nomeUsuario = user?.nome ? user.nome.split(' ')[0] : 'Colaborador';

    useEffect(() => {
        const handleResize = () => { 
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getMarginLeft = () => isMobile ? '0px' : (isCollapsed ? '80px' : '260px');

    // Busca o último erro registrado das automações de VT ao abrir a tela.
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch('/api/automacao-rh/ultimo-erro-vt', {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
                });
                if (!res.ok) return;
                const data = await res.json();
                if (data && data.existe) setUltimoErro(data);
            } catch (e) { /* silencioso: banner é informativo, não deve quebrar a tela */ }
        })();
    }, []);

    // Rótulo amigável da etapa onde o erro ocorreu.
    const rotuloEtapa = (rota) => rota === 'conciliacao'
        ? 'Conciliação (Etapa 2)'
        : rota === 'auditoria' ? 'Auditoria de Cadastro (Etapa 1)' : 'Automação de VT';

    const formatarQuando = (iso) => {
        try { return new Date(iso).toLocaleString('pt-BR'); } catch { return iso || ''; }
    };

    const [arquivoGeral, setArquivoGeral] = useState(null);
    const [arquivoVT, setArquivoVT] = useState(null);
    
    const [arquivosGVBUS, setArquivosGVBUS] = useState([]);
    const [arquivosPopulis, setArquivosPopulis] = useState([]);
    const [mesCompetencia, setMesCompetencia] = useState(String(new Date().getMonth() + 2).padStart(2, '0')); 
    const [anoCompetencia, setAnoCompetencia] = useState(String(new Date().getFullYear()));

    const [carregando, setCarregando] = useState(false);
    const [mensagem, setMensagem] = useState(null);
    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('Aguardando ficheiros...');
    const [isPolling, setIsPolling] = useState(false);
    const [executionId, setExecutionId] = useState(null);

    // Último erro registrado no servidor (persistido em disco). Serve para o RH
    // ver o que falhou por último mesmo sem uma execução ativa — pensado para o
    // período em que a Julia estiver de férias.
    const [ultimoErro, setUltimoErro] = useState(null);
    const [ultimoErroVisivel, setUltimoErroVisivel] = useState(true);

    // Polling de Progresso (Mantendo sua lógica de parsing de logs textuais)
    useEffect(() => {
        let interval;
        if (isPolling && executionId) {
            interval = setInterval(async () => {
                try {
                    const res = await fetch(`/api/automacao-rh/logs/${executionId}`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
                    });
                    const data = await res.json();
                    
                    if (data.logs && data.logs.length > 0) {
                        const lastLog = data.logs[data.logs.length - 1];
                        const cleanLog = lastLog.replace(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3}\s*-\s*INFO\s*-\s*|\[.*?\]\s*)/g, '');
                        setStatusText(cleanLog);

                        if (activeTab === 'auditoria') {
                            if (cleanLog.includes('Iniciando')) setProgress(10);
                            else if (cleanLog.includes('ler os dados')) setProgress(40);
                            else if (cleanLog.includes('cruzar bases')) setProgress(70);
                            else if (cleanLog.includes('ATENÇÃO') || cleanLog.includes('SUCESSO')) setProgress(100);
                        } else {
                            if (cleanLog.includes('Iniciando')) setProgress(5);
                            else if (cleanLog.includes('GVBUS')) setProgress(25);
                            else if (cleanLog.includes('Populis')) setProgress(45);
                            else if (cleanLog.includes('limpeza')) setProgress(60);
                            else if (cleanLog.includes('cruzar os dados')) setProgress(80);
                            else if (cleanLog.includes('TXT') || cleanLog.includes('empacotar')) setProgress(95);
                            else if (cleanLog.includes('Sucesso')) setProgress(100);
                        }
                    }
                } catch(e) {}
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isPolling, executionId, activeTab]);

    // --- FUNÇÃO LIMPAR ---
    const handleLimpar = () => {
        setArquivoGeral(null);
        setArquivoVT(null);
        setArquivosGVBUS([]);
        setArquivosPopulis([]);
        setMensagem(null);
        setProgress(0);
        setStatusText('Aguardando ficheiros...');
        setExecutionId(null);
        setCarregando(false);
        
        const inputs = ['input-geral', 'input-vt', 'input-gvbus', 'input-populis'];
        inputs.forEach(id => {
            const el = document.getElementById(id);
            if (el) el.value = '';
        });
    };

    const handleAuditoria = async (e) => {
        e.preventDefault();
        setCarregando(true); setMensagem(null); setProgress(5); setStatusText('A iniciar...');
        
        const newId = Date.now().toString();
        setExecutionId(newId); setIsPolling(true);

        const formData = new FormData();
        formData.append('executionId', newId);
        formData.append('arquivos_rh', arquivoGeral);
        formData.append('arquivos_rh', arquivoVT);

        try {
            const response = await fetch('/api/automacao-rh/rh-auditoria-vt', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                body: formData
            });

            const contentType = response.headers.get("content-type");
            if (contentType && contentType.indexOf("application/json") !== -1) {
                const data = await response.json();
                if (response.ok) {
                    setProgress(100); setStatusText('Auditoria Perfeita!');
                    setMensagem({ type: 'success', text: data.message });
                } else {
                    setMensagem({ type: 'error', text: data.error });
                }
            } else {
                const rawBlob = await response.blob();
                const excelBlob = new Blob([rawBlob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                let fileName = 'Divergencias_Sem_VT.xlsx';
                
                const url = window.URL.createObjectURL(excelBlob);
                const link = document.createElement('a');
                link.href = url; link.setAttribute('download', fileName);
                document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
                
                
                setProgress(100); setStatusText('Relatório Transferido!');
                setMensagem({ type: 'error', text: 'Atenção: Foram encontrados funcionários sem VT! O relatório foi transferido.' });
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro de ligação ao servidor.' });
        } finally {
            setCarregando(false); setTimeout(() => { setIsPolling(false); }, 3000);
        }
    };

    const handleConciliacao = async (e) => {
        e.preventDefault();
        setCarregando(true); setMensagem(null); setProgress(5); setStatusText('A preparar o pacote de ficheiros...');
        
        const newId = Date.now().toString();
        setExecutionId(newId); setIsPolling(true);

        const formData = new FormData();
        formData.append('executionId', newId);
        formData.append('mes', mesCompetencia);
        formData.append('ano', anoCompetencia);
        
        for (let i = 0; i < arquivosGVBUS.length; i++) formData.append('arquivos_gvbus', arquivosGVBUS[i]);
        for (let i = 0; i < arquivosPopulis.length; i++) formData.append('arquivos_populis', arquivosPopulis[i]);

        try {
            const response = await fetch('/api/automacao-rh/rh-conciliacao-vt', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                body: formData
            });

            if (response.ok) {
                const blob = await response.blob();
                let fileName = `Fechamento_VT_${mesCompetencia}_${anoCompetencia}.zip`;
                
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url; link.setAttribute('download', fileName);
                document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
                
                
                setProgress(100); setStatusText('ZIP Transferido!');
                setMensagem({ type: 'success', text: 'Fechamento concluído! O ZIP com o Excel e o TXT foi transferido.' });
            } else {
                const data = await response.json();
                setMensagem({ type: 'error', text: data.error || 'Erro na conciliação.' });
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro de ligação ao servidor.' });
        } finally {
            setCarregando(false); setTimeout(() => { setIsPolling(false); }, 3000);
        }
    };

    // Estilos Auxiliares
    const corModulo = '#e11d48'; 
    
    const getProgressBarColor = () => {
        if (mensagem?.type === 'error') return '#ef4444'; // Vermelho
        if (mensagem?.type === 'success') return '#10b981'; // Verde
        return corModulo; // Rosa (Processando)
    };

    const uploadAreaStyle = {
        border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '30px 15px', 
        textAlign: 'center', backgroundColor: '#f8fafc', cursor: 'pointer',
        transition: 'all 0.2s ease', position: 'relative'
    };

    return (
        <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
            {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

            <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease', width: '100%' }}>
                
                {/* CABEÇALHO PREMIUM */}
                {!embedded && (
                <div style={{
                    height: '85px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', display: 'flex', 
                    alignItems: 'center', justifyContent: 'space-between', 
                    padding: isMobile ? '0 20px 0 60px' : '0 40px', position: 'sticky', top: 0, zIndex: 20 
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button onClick={() => navigate('/automacoes')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                                <ArrowLeft size={14} /> Voltar
                            </button>
                            <span style={{ margin: '0 4px' }}>/</span> Automação & Processos <span style={{ margin: '0 4px' }}>/</span> RH
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Bus size={24} color={corModulo} /> Vale Transporte
                            </h2>
                            <button 
                                type="button"
                                onClick={() => setIsHelpOpen(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: '#fff1f2', color: corModulo, border: '1px solid #fecdd3', borderRadius: '99px', fontWeight: '600', cursor: 'pointer', fontSize: '12px', transition: 'all 0.2s' }}
                            >
                                <HelpCircle size={14} /> Como Utilizar
                            </button>
                        </div>
                    </div>
                    
                    {!isMobile && (
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#475569' }}>
                            Olá, {nomeUsuario} 👋
                        </span>
                    )}
                </div>
                )}

                <div style={{ padding: isMobile ? '20px' : '40px' }} className="max-w-5xl mx-auto">

                    {/* BANNER: último erro registrado (persistido no servidor). Informativo e
                        dispensável — não é o alerta da execução atual, é o histórico. */}
                    {ultimoErro && ultimoErroVisivel && !carregando && (
                        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '16px 18px', marginBottom: '24px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <AlertCircle size={20} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <History size={15} /> Último erro registrado
                                    </span>
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#b45309' }}>
                                        {rotuloEtapa(ultimoErro.rota)} · <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatarQuando(ultimoErro.quando)}</span>
                                    </span>
                                </div>
                                <p style={{ margin: '6px 0 0', fontSize: '13px', lineHeight: '1.6', color: '#78350f' }}>
                                    {ultimoErro.mensagem}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setUltimoErroVisivel(false)}
                                title="Dispensar"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b45309', padding: '2px', display: 'flex', flexShrink: 0 }}
                            >
                                <X size={18} />
                            </button>
                        </div>
                    )}

                    <div style={{ backgroundColor: '#fff', padding: isMobile ? '20px' : '30px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
                        
                        {/* ABAS */}
                        <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '30px' }}>
                            <button 
                                onClick={() => { setActiveTab('auditoria'); setMensagem(null); }}
                                style={{ flex: 1, padding: '12px', fontSize: '15px', fontWeight: '600', borderBottom: activeTab === 'auditoria' ? `3px solid ${corModulo}` : 'none', color: activeTab === 'auditoria' ? corModulo : '#64748b', backgroundColor: 'transparent', cursor: 'pointer', transition: 'all 0.2s' }}
                            >
                                Etapa 1: Auditoria de Cadastro
                            </button>
                            <button 
                                onClick={() => { setActiveTab('conciliacao'); setMensagem(null); }}
                                style={{ flex: 1, padding: '12px', fontSize: '15px', fontWeight: '600', borderBottom: activeTab === 'conciliacao' ? `3px solid ${corModulo}` : 'none', color: activeTab === 'conciliacao' ? corModulo : '#64748b', backgroundColor: 'transparent', cursor: 'pointer', transition: 'all 0.2s' }}
                            >
                                Etapa 2: Conciliação e TXT
                            </button>
                        </div>

                        {/* CONTEÚDO: AUDITORIA */}
                        {activeTab === 'auditoria' && (
                            <form onSubmit={handleAuditoria}>
                                <div style={{ backgroundColor: '#fff1f2', borderLeft: `4px solid ${corModulo}`, padding: '16px 20px', borderRadius: '8px', marginBottom: '24px' }}>
                                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#be123c', fontSize: '15px', fontWeight: '700', marginBottom: '8px', margin: 0 }}><Info size={18} /> Auditoria</h4>
                                    <p style={{ color: '#9f1239', fontSize: '14px', lineHeight: '1.6', margin: 0, marginTop: '4px' }}>Cruza a listagem Geral com o Cadastro VT para identificar colaboradores ativos admitidos recentemente sem o benefício.</p>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                                    
                                    {/* Ficheiro Geral */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}> Ficheiro Geral (RH)</label>
                                        <div style={uploadAreaStyle}>
                                            <input id="input-geral" type="file" accept=".xlsx, .xls" onChange={(e) => { setArquivoGeral(e.target.files[0]); setMensagem(null); }} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} disabled={carregando} required />
                                            {!arquivoGeral ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                    <UploadCloud size={28} color="#94a3b8" />
                                                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#475569' }}>Selecionar Planilha Geral</p>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                    <FileSpreadsheet size={28} color="#10b981" />
                                                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{arquivoGeral.name}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Ficheiro VT */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}> Ficheiro Cadastro VT</label>
                                        <div style={uploadAreaStyle}>
                                            <input id="input-vt" type="file" accept=".xlsx, .xls" onChange={(e) => { setArquivoVT(e.target.files[0]); setMensagem(null); }} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} disabled={carregando} required />
                                            {!arquivoVT ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                    <UploadCloud size={28} color="#94a3b8" />
                                                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#475569' }}>Selecionar Planilha VT</p>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                    <FileSpreadsheet size={28} color="#10b981" />
                                                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{arquivoVT.name}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
                                    <button type="button" onClick={handleLimpar} disabled={carregando} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '8px', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '600', border: '1px solid #cbd5e1', cursor: carregando ? 'not-allowed' : 'pointer' }}>
                                        <Eraser size={18} /> Limpar
                                    </button>
                                    <button type="submit" disabled={carregando || !arquivoGeral || !arquivoVT} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '8px', backgroundColor: carregando || !arquivoGeral || !arquivoVT ? '#cbd5e1' : corModulo, color: '#fff', fontWeight: '600', border: 'none', cursor: carregando || !arquivoGeral || !arquivoVT ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s', boxShadow: '0 4px 6px -1px rgba(225, 29, 72, 0.2)' }}>
                                        {carregando ? <><Loader2 size={18} className="animate-spin" /> Auditando...</> : <><Download size={18} /> Iniciar Auditoria</>}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* CONTEÚDO: CONCILIAÇÃO */}
                        {activeTab === 'conciliacao' && (
                            <form onSubmit={handleConciliacao}>
                                <div style={{ backgroundColor: '#fff1f2', borderLeft: `4px solid ${corModulo}`, padding: '16px 20px', borderRadius: '8px', marginBottom: '24px' }}>
                                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#be123c', fontSize: '15px', fontWeight: '700', marginBottom: '8px', margin: 0 }}><FileText size={18} /> Conciliação e Fechamento</h4>
                                    <p style={{ color: '#9f1239', fontSize: '14px', lineHeight: '1.6', margin: 0, marginTop: '4px' }}>Cruza os relatórios da Transportadora (GVBUS) com o Populis (Benefícios), calcula as diferenças proporcionais e gera o ficheiro TXT de integração.</p>
                                </div>

                                {/* Controles de Data */}
                                <div style={{ display: 'flex', gap: '15px', marginBottom: '24px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Mês de Competência:</label>
                                        <select value={mesCompetencia} onChange={e => setMesCompetencia(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff', outline: 'none' }}>
                                            <option value="01">01 - Janeiro</option><option value="02">02 - Fevereiro</option><option value="03">03 - Março</option>
                                            <option value="04">04 - Abril</option><option value="05">05 - Maio</option><option value="06">06 - Junho</option>
                                            <option value="07">07 - Julho</option><option value="08">08 - Agosto</option><option value="09">09 - Setembro</option>
                                            <option value="10">10 - Outubro</option><option value="11">11 - Novembro</option><option value="12">12 - Dezembro</option>
                                        </select>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Ano de Competência:</label>
                                        <input type="number" value={anoCompetencia} onChange={e => setAnoCompetencia(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff', outline: 'none' }} />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                                    
                                    {/* Múltiplos Ficheiros GVBUS */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}> Ficheiros GVBUS (Transportadora)</label>
                                        <div style={uploadAreaStyle}>
                                            <input id="input-gvbus" type="file" multiple accept=".csv, .xlsx" onChange={(e) => { setArquivosGVBUS(e.target.files); setMensagem(null); }} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} disabled={carregando} required />
                                            {arquivosGVBUS.length === 0 ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                    <UploadCloud size={28} color="#94a3b8" />
                                                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#475569' }}>Selecionar Ficheiros GVBUS</p>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                    <FileSpreadsheet size={28} color={corModulo} />
                                                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: corModulo }}>{arquivosGVBUS.length} arquivo(s) selecionado(s)</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Múltiplos Ficheiros Populis */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <label style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}> Ficheiros Populis (Benefícios)</label>
                                        <div style={uploadAreaStyle}>
                                            <input id="input-populis" type="file" multiple accept=".xlsx" onChange={(e) => { setArquivosPopulis(e.target.files); setMensagem(null); }} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} disabled={carregando} required />
                                            {arquivosPopulis.length === 0 ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                    <UploadCloud size={28} color="#94a3b8" />
                                                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#475569' }}>Selecionar Ficheiros Populis</p>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                    <FileSpreadsheet size={28} color={corModulo} />
                                                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: corModulo }}>{arquivosPopulis.length} arquivo(s) selecionado(s)</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
                                    <button type="button" onClick={handleLimpar} disabled={carregando} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '8px', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '600', border: '1px solid #cbd5e1', cursor: carregando ? 'not-allowed' : 'pointer' }}>
                                        <Eraser size={18} /> Limpar
                                    </button>
                                    <button type="submit" disabled={carregando || arquivosGVBUS.length === 0 || arquivosPopulis.length === 0} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '8px', backgroundColor: carregando || arquivosGVBUS.length === 0 || arquivosPopulis.length === 0 ? '#cbd5e1' : corModulo, color: '#fff', fontWeight: '600', border: 'none', cursor: carregando || arquivosGVBUS.length === 0 || arquivosPopulis.length === 0 ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s', boxShadow: '0 4px 6px -1px rgba(225, 29, 72, 0.2)' }}>
                                        {carregando ? <><Loader2 size={18} className="animate-spin" /> Conciliando...</> : <><Download size={18} /> Gerar Excel e TXT (ZIP)</>}
                                    </button>
                                </div>
                            </form>
                        )}
                        
                        {/* ALERTAS (Substitui o bloco antigo de mensagem) */}
                        {mensagem && !carregando && (
                            <div style={{ backgroundColor: mensagem.type === 'success' ? '#ecfdf5' : '#fef2f2', border: `1px solid ${mensagem.type === 'success' ? '#a7f3d0' : '#fecaca'}`, color: mensagem.type === 'success' ? '#059669' : '#ef4444', padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', marginTop: '24px', fontSize: '14px', fontWeight: '500' }}>
                                {mensagem.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />} 
                                {mensagem.text}
                            </div>
                        )}
                    </div>

                    {/* CARD DE PROGRESSO (Substituindo a barra inline) */}
                    {(carregando || progress > 0) && (
                        <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {carregando && <Loader2 size={24} color="#0ea5e9" className="animate-spin" />}
                                    {!carregando && mensagem?.type === 'success' && <CheckCircle2 size={24} color="#10b981" />}
                                    {!carregando && mensagem?.type === 'error' && <AlertCircle size={24} color="#ef4444" />}
                                    
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Status da Execução</h4>
                                        <p style={{ margin: 0, fontSize: '14px', color: '#64748b', marginTop: '2px' }}>{statusText}</p>
                                    </div>
                                </div>
                                <span style={{ fontSize: '20px', fontWeight: '800', color: getProgressBarColor() }}>
                                    {progress}%
                                </span>
                            </div>

                            <div style={{ width: '100%', height: '12px', backgroundColor: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                                <div style={{ 
                                    width: `${progress}%`, 
                                    height: '100%', 
                                    backgroundColor: getProgressBarColor(),
                                    borderRadius: '99px',
                                    transition: 'width 0.5s ease-in-out, background-color 0.3s ease'
                                }} />
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* CHAMADA DO MANUAL COM FILTRO EXCLUSIVO PARA VALE TRANSPORTE */}
            <HelpCenter 
                isOpen={isHelpOpen} 
                onClose={() => setIsHelpOpen(false)} 
                secaoInicial="rh-auditoria-vt" 
                showOnlyIds={['rh-auditoria-vt']} 
            />
        </div>
    );
};

export default AutomacaoValeTransporte;